import React, { useId, useMemo, useState } from 'react';
import { Story } from '../../context/AppContext';
import { formatDurationLabel } from '../../utils/duration';
import StoryArtwork from '../StoryArtwork/StoryArtwork';
import { FormField, PillButton, Sheet } from '../ui';
import { emptyStoryFilters, groupStoryWorlds, matchesStory, StorySearchFilters } from './storyWorldModel';
import './StorySearch.css';

interface Props { stories: Story[]; library: boolean; onClose: () => void; onSelect: (story: Story) => void; }

const LENGTHS: Array<[StorySearchFilters['duration'], string]> = [
  ['', 'Any length'],
  ['up_to_5', '5 minutes or less'],
  ['6_to_10', '6-10 minutes'],
  ['11_to_15', '11-15 minutes'],
  ['16_plus', '16+ minutes'],
];

/**
 * Search and filters in a sheet. Matching, grouping and selection are the
 * existing model's; the caller keeps the `?story=` contract.
 */
const StorySearch: React.FC<Props> = ({ stories, library, onClose, onSelect }) => {
  const id = useId();
  const [filters, setFilters] = useState<StorySearchFilters>(emptyStoryFilters);
  const update = (key: keyof StorySearchFilters, value: string) => setFilters((current) => ({ ...current, [key]: value }));
  const results = useMemo(() => groupStoryWorlds(stories.filter((story) => matchesStory(story, filters))), [stories, filters]);
  const ages = Array.from(new Set(stories.map((story) => story.age_range).filter(Boolean))).sort();
  const moods = Array.from(new Set(stories.flatMap((story) => [story.theme, ...(story.tags || [])]).filter((value): value is string => Boolean(value)))).sort();
  const count = results.reduce((sum, world) => sum + world.chapters.length, 0);
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <Sheet open title={library ? 'Search your stories' : 'Search Discover'} closeLabel="Close search" onClose={onClose} className="luma-search">
      <FormField id={`${id}-query`} label="Search stories" className="luma-search__query">
        <input type="search" maxLength={100} value={filters.query} onChange={(event) => update('query', event.target.value)} placeholder="Find a story or character…" autoComplete="off" />
      </FormField>
      <div className="luma-search__filters">
        <FormField id={`${id}-age`} label="Age">
          {(control) => (
            <select {...control} value={filters.age} onChange={(event) => update('age', event.target.value)}>
              <option value="">All ages</option>
              {ages.map((age) => <option key={age} value={age}>{age}</option>)}
            </select>
          )}
        </FormField>
        <FormField id={`${id}-mood`} label="Mood">
          {(control) => (
            <select {...control} value={filters.mood} onChange={(event) => update('mood', event.target.value)}>
              <option value="">All moods</option>
              {moods.map((mood) => <option key={mood} value={mood}>{mood}</option>)}
            </select>
          )}
        </FormField>
      </div>
      <fieldset className="luma-search__length">
        <legend>Length</legend>
        <div className="luma-chip-row">
          {LENGTHS.map(([value, label]) => (
            <label className="luma-chip-radio" key={value || 'any'}>
              <input type="radio" name={`${id}-length`} value={value} checked={filters.duration === value} onChange={() => update('duration', value)} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="luma-search__count">
        <p role="status">{count} results</p>
        {hasFilters && <PillButton variant="secondary" size="compact" onClick={() => setFilters(emptyStoryFilters)}>Clear search and filters</PillButton>}
      </div>
      {results.length === 0 && (
        <div className="luma-search__empty">
          <h3>No stories found</h3>
          <p>Try a different word or clear a filter.</p>
        </div>
      )}
      {results.map((world) => (
        <section className="luma-search__group" key={world.id} aria-label={world.title}>
          {world.serialized && <h3 className="luma-search__group-title">{world.title}</h3>}
          {world.chapters.map((story) => (
            <button type="button" className="luma-search__result" key={story.story_id} onClick={() => onSelect(story)}>
              <StoryArtwork src={story.thumbnail_url} srcSet={story.thumbnail_srcset} sizes="72px" loading="lazy" imageClassName="luma-search__art" fallbackClassName="luma-search__art" fallbackSize={22} />
              <span className="luma-search__result-text">
                {world.serialized && <small>Chapter {story.chapter_number}</small>}
                <strong>{story.chapter_title || story.title}</strong>
                <span className="luma-search__result-summary">{story.description || story.summary}</span>
                <small>{formatDurationLabel(story.estimated_duration_minutes)}</small>
              </span>
            </button>
          ))}
        </section>
      ))}
    </Sheet>
  );
};

export default StorySearch;
